import { useContext } from 'react';
import { FeatureContext, FeatureContextValue } from '../contexts/featureContext';
import { FeatureCode } from '../common/constants/featureCodes';
import { FeatureWithUsage, FeatureCheckResult, FeatureUsageInfo } from '../type/feature-types';

/**
 * Main hook to access all feature context functionality.
 * Used by FeatureGate, FeatureButton, and page-level components.
 *
 * @userstory US-FG-01, US-FG-02, US-FG-03, US-FG-09, US-FG-10
 */
export const useFeatures = (): FeatureContextValue => {
	const context = useContext(FeatureContext);
	if (!context) {
		throw new Error('useFeatures must be used within a FeatureProvider');
	}
	return context;
};

/**
 * Hook to get a single feature and its status.
 * Returns the feature object, canUse boolean, check result, and usage info.
 *
 * @userstory US-FG-01, US-FG-02, US-FG-03, US-FG-09, US-FG-10
 */
export const useFeature = (
	code: FeatureCode | string,
): {
	feature: FeatureWithUsage | undefined;
	canUse: boolean;
	checkResult: FeatureCheckResult;
	usage: FeatureUsageInfo | null;
} => {
	const { getFeature, canUse, checkFeature, getUsage } = useFeatures();

	return {
		feature: getFeature(code),
		canUse: canUse(code),
		checkResult: checkFeature(code),
		usage: getUsage(code),
	};
};

/**
 * Simple hook to check if a feature can be used.
 * Returns true only if the feature is enabled and within usage limits.
 *
 * @userstory US-FG-02, US-FG-10
 */
export const useCanUseFeature = (code: FeatureCode | string): boolean => {
	const { canUse } = useFeatures();
	return canUse(code);
};

/**
 * Hook to get usage info for a limited feature.
 * Returns used/limit/remaining counts for limit-type features.
 *
 * @userstory US-FG-03
 */
export const useFeatureUsage = (code: FeatureCode | string): FeatureUsageInfo | null => {
	const { getUsage } = useFeatures();
	return getUsage(code);
};

/**
 * Hook that provides a function to execute an action with feature check.
 * Returns a function that:
 * 1. Checks if feature can be used
 * 2. If yes, executes the action and optionally increments usage
 * 3. If no, calls the onBlocked callback
 *
 * @userstory US-FG-02, US-FG-03, US-FG-10
 */
export const useFeatureAction = (
	code: FeatureCode | string,
	options?: {
		trackUsage?: boolean;
		onBlocked?: (result: FeatureCheckResult) => void;
	},
) => {
	const { checkFeature, incrementLocalUsage } = useFeatures();

	const executeWithCheck = async <T>(action: () => Promise<T> | T): Promise<T | null> => {
		const result = checkFeature(code);

		if (!result.allowed) {
			options?.onBlocked?.(result);
			return null;
		}

		const actionResult = await action();

		// Optimistically increment usage on success
		if (options?.trackUsage) {
			incrementLocalUsage(code);
		}

		return actionResult;
	};

	return { executeWithCheck, checkFeature: () => checkFeature(code) };
};
